import * as React from 'react';
import { Button, Stack, Box } from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useColorMode } from "../ColorModeContext";
import { grey, red, blue } from '@mui/material/colors';

interface GalleryProps {
  images: string[];
  onDeleteImage: (index: number) => void; // Function to delete an image
  onDownloadImage: (img: string) => void; // Function to download an image
}

const Gallery: React.FC<GalleryProps> = ({ images, onDeleteImage, onDownloadImage }) => {
  const { colorMode } = useColorMode();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [currentImage, setCurrentImage] = React.useState<string | null>(null); // Stores the image being viewed in the lightbox
  
  const handleThumbnailClick = (img: string) => {
    setCurrentImage(img);
    setIsOpen(true); // Open lightbox when image is clicked
  };
  
  return (
    <Stack spacing={2}>
      {images.map((img, index) => (
        <Box
          key={index}
          sx={{ display: "flex", flexDirection: "row", p: "10px 0px", height: "200px" }}
        >
          {/* Image Section */}
          <Box sx={{ flex: ".8" }}>
            <img
              src={img}
              alt={`Gallery-${index}`}
              style={{
                borderRadius: "10px",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                cursor: "pointer",
              }}
              onClick={() => handleThumbnailClick(img)} // Open lightbox on click
            />
          </Box>

          {/* Buttons Section */}
          <Box display="flex" flexDirection="column" sx={{ flex: ".2", ml: "10px" }}>
            {/* Download Button */}
            <Button
              variant="contained"
              sx={{
                mb: "10px",
                width: "100%",
                height: "50%",
                color: colorMode ? grey[300] : grey[600],
                backgroundColor: colorMode ? grey[900] : grey[300],
                '&:hover': {
                  color: colorMode ? grey[50] : grey[300],
                  backgroundColor: colorMode ? blue[600] : blue[600]
                }
              }}
              onClick={() => onDownloadImage(img)} // Trigger download on click
            >
              <DownloadIcon />
            </Button>

            {/* Delete Button */}
            <Button
              variant="contained"
              sx={{
                width: "100%",
                height: "50%",
                color: colorMode ? grey[300] : grey[600],
                backgroundColor: colorMode ? grey[900] : grey[300],
                '&:hover': {
                  color: colorMode ? grey[50] : grey[300],
                  backgroundColor: colorMode ? red[600] : red[400]
                }
              }}
              onClick={() => onDeleteImage(index)} // Trigger delete on click
            >
              <DeleteIcon />
            </Button>
          </Box>
        </Box>
      ))}

      {/* Lightbox Full-Screen Mode */}
      {isOpen && currentImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsOpen(false)}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "white",
              fontSize: "24px",
            }}
          >
            X {/* You can replace this with a custom CloseIcon */}
          </button>

          <img
            src={currentImage}
            alt="Full Screen"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "10px",
              objectFit: "contain",
            }}
          />
        </div>
      )}
    </Stack>
  );
};

export default Gallery;
